import { useEffect, useRef, useState } from "react";
import { Loader2, Save, Plus, Trash2, Upload, Palette, Type, Images, LayoutGrid, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fullUrl } from "@/context/SiteConfigContext";
import { PageHeader } from "@/components/admin/ui";

const inputCls =
  "w-full rounded-xl border border-white/10 bg-[#0A0B0D] px-4 py-3 text-sm text-white placeholder-zinc-500 outline-none focus:border-[#FFB800]";

const COLOR_FIELDS = [
  { key: "primary", label: "Amarelo principal (botões, destaques)" },
  { key: "dark_bg", label: "Fundo escuro das seções" },
  { key: "fire", label: "Vermelho fogo (brilho de fundo)" },
  { key: "whatsapp", label: "Verde do WhatsApp" },
  { key: "danger", label: "Vermelho de alertas" },
];

const TEXT_FIELDS = [
  { key: "hero_badge", label: "Selo acima do título" },
  { key: "hero_line1", label: "Título — linha 1 (branca)" },
  { key: "hero_line2", label: "Título — linha 2 (destaque)" },
  { key: "hero_line3", label: "Título — linha 3 (destaque)" },
];

const SERVICE_SLOTS = [
  { key: "fogao", label: "Conversão de Fogão" },
  { key: "aquecedor", label: "Instalação de Aquecedor" },
  { key: "estanqueidade", label: "Teste de Estanqueidade" },
  { key: "tubulacao", label: "Tubulação de Gás" },
  { key: "laudos", label: "Laudos Técnicos" },
  { key: "manutencao", label: "Manutenção Preventiva" },
];

const DEFAULT_SERVICE_IMG = {
  fogao: "/assets/serv-fogao.jpg",
  aquecedor: "/assets/serv-aquecedor.jpg",
  estanqueidade: "/assets/serv-estanqueidade.jpg",
  tubulacao: "/assets/serv-tubulacao.jpg",
  laudos: "/assets/serv-laudos.jpg",
  manutencao: "/assets/serv-manutencao.jpg",
};

const PRESETS = [
  { name: "Fogo", values: { primary: "#FFB800", dark_bg: "#0A0B0D", fire: "#E5484D", fire_opacity: 0.25, whatsapp: "#25D366", danger: "#E5484D" } },
  { name: "Original", values: { primary: "#FFB800", dark_bg: "#0A0B0D", fire: "#E5484D", fire_opacity: 0, whatsapp: "#25D366", danger: "#E5484D" } },
  { name: "Noturno", values: { primary: "#FF8C3B", dark_bg: "#05070C", fire: "#B91C1C", fire_opacity: 0.3, whatsapp: "#22C55E", danger: "#EF4444" } },
  { name: "Brasa", values: { primary: "#FFC933", dark_bg: "#140A08", fire: "#DC2626", fire_opacity: 0.35, whatsapp: "#25D366", danger: "#E5484D" } },
];

const TABS = [
  ["visual", "Visual & Topo"],
  ["textos", "Textos das Seções"],
  ["previa", "Prévia ao Vivo"],
];

export default function ThemePage() {
  const [cfg, setCfg] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const [tab, setTab] = useState("visual");
  const slideInput = useRef(null);
  const serviceInput = useRef(null);
  const serviceSlot = useRef("");
  const previewRef = useRef(null);

  useEffect(() => {
    if (!cfg || tab !== "previa") return undefined;
    const t = setTimeout(() => {
      previewRef.current?.contentWindow?.postMessage(
        { type: "manutentudo-theme-preview", payload: cfg },
        window.location.origin
      );
    }, 400);
    return () => clearTimeout(t);
  }, [cfg, tab]);

  const postPreview = () => {
    setTimeout(() => {
      previewRef.current?.contentWindow?.postMessage(
        { type: "manutentudo-theme-preview", payload: cfg },
        window.location.origin
      );
    }, 900);
  };

  const applyPreset = (p) => {
    setCfg((c) => ({ ...c, ...p.values }));
    toast.success(`Tema "${p.name}" aplicado — salve para publicar`);
  };

  const setListItem = (key, idx, field, value) =>
    setCfg((c) => {
      const arr = [...(c[key] || [])];
      arr[idx] = field ? { ...arr[idx], [field]: value } : value;
      return { ...c, [key]: arr };
    });

  useEffect(() => {
    api.get("/site-config").then((r) => {
      setCfg(r.data);
      setLastSaved(r.data);
    }).catch(() => {});
  }, []);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const upload = async (file, done) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/uploads", fd);
    done(data.url);
  };

  const onSlideFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading("slide");
    try {
      await upload(file, (url) => set("hero_slides", [...(cfg.hero_slides || []), url]));
      toast.success("Foto adicionada ao topo — salve para aplicar");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading("");
    }
  };

  const onServiceFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const slot = serviceSlot.current;
    setUploading(slot);
    try {
      await upload(file, (url) => set("service_images", { ...(cfg.service_images || {}), [slot]: url }));
      toast.success("Foto do serviço atualizada — salve para aplicar");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading("");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/site-config", cfg);
      setLastSaved(cfg);
      toast.success("Personalização salva! Abra o site para ver.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const dirty = cfg && lastSaved && JSON.stringify(cfg) !== JSON.stringify(lastSaved);

  const undo = () => {
    setCfg(lastSaved);
    toast.success("Alterações descartadas — voltou ao último salvo");
  };

  if (!cfg) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 size={28} className="animate-spin text-[#FFB800]" />
      </div>
    );
  }

  return (
    <div data-testid="theme-page">
      <PageHeader title="Personalizar Site" sub="Cores, textos e imagens — tudo ajustável e valendo na hora" testId="theme-header">
        <button
          onClick={undo}
          disabled={!dirty}
          data-testid="theme-undo-button"
          className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 font-display text-sm font-bold uppercase tracking-wider text-zinc-300 transition-colors hover:border-[#FFB800]/60 hover:text-[#FFB800] disabled:opacity-40"
        >
          <Undo2 size={15} />
          Desfazer
        </button>
        <button
          onClick={save}
          disabled={saving}
          data-testid="theme-save-button"
          className="flex items-center gap-2 rounded-full bg-[#FFB800] px-5 py-2.5 font-display text-sm font-bold uppercase tracking-wider text-black transition-transform hover:scale-105 disabled:opacity-60"
        >
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
          {saving ? "Salvando..." : "Salvar Tudo"}
        </button>
      </PageHeader>

      <div className="mb-6 flex flex-wrap gap-2" data-testid="theme-tabs">
        {TABS.map(([v, l]) => (
          <button
            key={v}
            onClick={() => setTab(v)}
            data-testid={`theme-tab-${v}`}
            className={`rounded-full px-5 py-2.5 font-display text-sm font-bold uppercase tracking-wider transition-colors ${
              tab === v ? "bg-[#FFB800] text-black" : "border border-white/10 text-zinc-400 hover:border-[#FFB800]/50 hover:text-white"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      {tab === "visual" && (
      <>
      <div className="mb-5 flex flex-wrap items-center gap-3 rounded-2xl border border-white/5 bg-[#11141A] p-4" data-testid="theme-presets">
        <span className="text-xs font-bold uppercase tracking-wider text-zinc-400">Temas prontos:</span>
        {PRESETS.map((p) => (
          <button
            key={p.name}
            onClick={() => applyPreset(p)}
            data-testid={`theme-preset-${p.name.toLowerCase()}`}
            className="rounded-full border border-white/10 px-4 py-2 text-xs font-bold uppercase tracking-wider text-zinc-300 transition-colors hover:border-[#FFB800] hover:text-[#FFB800]"
          >
            {p.name}
          </button>
        ))}
        <span className="text-[11px] text-zinc-600">aplica na hora — clique em Salvar Tudo para publicar</span>
      </div>
      <div className="grid gap-5 xl:grid-cols-2">
        <div className="rounded-2xl border border-white/5 bg-[#11141A] p-6" data-testid="theme-colors-card">
          <h3 className="flex items-center gap-2 font-display text-lg font-bold uppercase text-white">
            <Palette size={18} className="text-[#FFB800]" /> Cores do site
          </h3>
          <div className="mt-5 space-y-4">
            {COLOR_FIELDS.map((f) => (
              <div key={f.key} className="flex items-center gap-4">
                <input
                  type="color"
                  value={cfg[f.key]}
                  onChange={(e) => set(f.key, e.target.value)}
                  data-testid={`theme-color-${f.key}`}
                  className="h-11 w-14 shrink-0 cursor-pointer rounded-lg border border-white/10 bg-transparent"
                />
                <div className="flex-1">
                  <p className="text-sm text-zinc-300">{f.label}</p>
                  <p className="text-[11px] uppercase text-zinc-600">{cfg[f.key]}</p>
                </div>
              </div>
            ))}
            <div>
              <div className="flex items-center justify-between">
                <label className="text-sm text-zinc-300">Intensidade do brilho de fogo</label>
                <span className="rounded-lg border border-white/10 bg-[#0A0B0D] px-3 py-1 font-display text-sm font-bold text-[#FFB800]" data-testid="theme-fire-opacity-value">
                  {Math.round((cfg.fire_opacity ?? 0.22) * 100)}%
                </span>
              </div>
              <input
                type="range"
                min={0}
                max={60}
                value={Math.round((cfg.fire_opacity ?? 0.22) * 100)}
                onChange={(e) => set("fire_opacity", Number(e.target.value) / 100)}
                data-testid="theme-fire-opacity-slider"
                className="mt-3 w-full accent-[#FFB800]"
              />
              <p className="mt-1 text-xs text-zinc-600">0% some com o brilho vermelho · deixe baixo para não atrapalhar a leitura</p>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-white/5 bg-[#11141A] p-6" data-testid="theme-texts-card">
          <h3 className="flex items-center gap-2 font-display text-lg font-bold uppercase text-white">
            <Type size={18} className="text-[#FFB800]" /> Textos do topo
          </h3>
          <div className="mt-5 space-y-4">
            {TEXT_FIELDS.map((f) => (
              <div key={f.key}>
                <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{f.label}</label>
                <input
                  value={cfg[f.key]}
                  onChange={(e) => set(f.key, e.target.value)}
                  data-testid={`theme-text-${f.key}`}
                  className={`${inputCls} mt-1.5`}
                />
              </div>
            ))}
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Subtítulo</label>
              <textarea
                rows={3}
                value={cfg.hero_subtitle}
                onChange={(e) => set("hero_subtitle", e.target.value)}
                data-testid="theme-text-hero_subtitle"
                className={`${inputCls} mt-1.5 resize-none`}
              />
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-white/5 bg-[#11141A] p-6" data-testid="theme-slides-card">
          <div className="flex items-center justify-between">
            <h3 className="flex items-center gap-2 font-display text-lg font-bold uppercase text-white">
              <Images size={18} className="text-[#FFB800]" /> Fotos do topo ({(cfg.hero_slides || []).length}/10)
            </h3>
            <input ref={slideInput} type="file" accept="image/*" onChange={onSlideFile} className="hidden" data-testid="theme-slide-input" />
            <button
              onClick={() => slideInput.current?.click()}
              disabled={uploading === "slide" || (cfg.hero_slides || []).length >= 10}
              data-testid="theme-slide-upload"
              className="flex items-center gap-2 rounded-full border border-[#FFB800]/50 px-4 py-2 text-xs font-bold uppercase tracking-wider text-[#FFB800] transition-colors hover:bg-[#FFB800]/10 disabled:opacity-50"
            >
              {uploading === "slide" ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} strokeWidth={3} />}
              Adicionar
            </button>
          </div>
          <p className="mt-1 text-xs text-zinc-500">Passam sozinhas com animações aleatórias. Vazio = usa as fotos padrão.</p>
          <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-5">
            {(cfg.hero_slides || []).map((url, i) => (
              <div key={url} className="group relative overflow-hidden rounded-xl border border-white/10" data-testid={`theme-slide-${i}`}>
                <img src={fullUrl(url)} alt={`Foto ${i + 1}`} className="aspect-square w-full object-cover" />
                <span className="absolute left-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#FFB800] font-display text-[10px] font-bold text-black">{i + 1}</span>
                <button
                  onClick={() => set("hero_slides", cfg.hero_slides.filter((u) => u !== url))}
                  data-testid={`theme-slide-delete-${i}`}
                  className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-red-500/90 text-white opacity-0 transition-opacity group-hover:opacity-100"
                  aria-label="Remover"
                >
                  <Trash2 size={11} />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-white/5 bg-[#11141A] p-6" data-testid="theme-services-card">
          <h3 className="flex items-center gap-2 font-display text-lg font-bold uppercase text-white">
            <LayoutGrid size={18} className="text-[#FFB800]" /> Fotos dos serviços
          </h3>
          <input ref={serviceInput} type="file" accept="image/*" onChange={onServiceFile} className="hidden" data-testid="theme-service-input" />
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {SERVICE_SLOTS.map((slot) => {
              const current = fullUrl((cfg.service_images || {})[slot.key]) || DEFAULT_SERVICE_IMG[slot.key];
              return (
                <div key={slot.key} className="group relative overflow-hidden rounded-xl border border-white/10" data-testid={`theme-service-${slot.key}`}>
                  <img src={current} alt={slot.label} className="aspect-[4/3] w-full object-cover" />
                  <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 bg-gradient-to-t from-black/85 to-transparent p-2.5">
                    <span className="text-[10px] font-semibold leading-tight text-white">{slot.label}</span>
                    <button
                      onClick={() => { serviceSlot.current = slot.key; serviceInput.current?.click(); }}
                      data-testid={`theme-service-upload-${slot.key}`}
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#FFB800] text-black transition-transform hover:scale-110"
                      aria-label={`Trocar foto de ${slot.label}`}
                    >
                      {uploading === slot.key ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      </>
      )}

      {tab === "textos" && (
        <div className="space-y-5">
          <div className="rounded-2xl border border-white/5 bg-[#11141A] p-6" data-testid="theme-about-card">
            <h3 className="font-display text-lg font-bold uppercase text-white">
              Seção <span className="italic text-[#FFB800]">Sobre</span>
            </h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Título (parte preta)</label>
                <input value={cfg.about_title || ""} onChange={(e) => set("about_title", e.target.value)} data-testid="theme-about-title" className={`${inputCls} mt-1.5`} />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Título (parte amarela)</label>
                <input value={cfg.about_accent || ""} onChange={(e) => set("about_accent", e.target.value)} data-testid="theme-about-accent" className={`${inputCls} mt-1.5`} />
              </div>
            </div>
            <div className="mt-5 space-y-3">
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Parágrafos — use **texto** para negrito</label>
              {(cfg.about_paragraphs || []).map((p, i) => (
                <div key={i} className="flex items-start gap-2">
                  <textarea rows={2} value={p} onChange={(e) => setListItem("about_paragraphs", i, null, e.target.value)} data-testid={`theme-about-p-${i}`} className={`${inputCls} resize-none`} />
                  <button onClick={() => set("about_paragraphs", cfg.about_paragraphs.filter((_, j) => j !== i))} data-testid={`theme-about-p-delete-${i}`} className="mt-2 text-zinc-600 transition-colors hover:text-red-400" aria-label="Remover parágrafo">
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
              <button onClick={() => set("about_paragraphs", [...(cfg.about_paragraphs || []), ""])} data-testid="theme-about-p-add" className="text-xs font-bold uppercase tracking-wider text-[#FFB800]">
                + Adicionar parágrafo
              </button>
            </div>
            <div className="mt-5 space-y-3">
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Diferenciais (lista com bolinhas)</label>
              {(cfg.about_points || []).map((p, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input value={p} onChange={(e) => setListItem("about_points", i, null, e.target.value)} data-testid={`theme-about-point-${i}`} className={inputCls} />
                  <button onClick={() => set("about_points", cfg.about_points.filter((_, j) => j !== i))} className="text-zinc-600 transition-colors hover:text-red-400" aria-label="Remover diferencial">
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
              <button onClick={() => set("about_points", [...(cfg.about_points || []), ""])} data-testid="theme-about-point-add" className="text-xs font-bold uppercase tracking-wider text-[#FFB800]">
                + Adicionar diferencial
              </button>
            </div>
          </div>

          <div className="rounded-2xl border border-white/5 bg-[#11141A] p-6" data-testid="theme-services-text-card">
            <h3 className="font-display text-lg font-bold uppercase text-white">
              Serviços <span className="italic text-[#FFB800]">(títulos e descrições)</span>
            </h3>
            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              {(cfg.services || []).map((s, i) => (
                <div key={s.key} className="rounded-xl border border-white/10 p-4" data-testid={`theme-service-text-${i}`}>
                  <input value={s.title} onChange={(e) => setListItem("services", i, "title", e.target.value)} data-testid={`theme-service-title-${i}`} className={inputCls} />
                  <textarea rows={2} value={s.desc} onChange={(e) => setListItem("services", i, "desc", e.target.value)} data-testid={`theme-service-desc-${i}`} className={`${inputCls} mt-2 resize-none`} />
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-white/5 bg-[#11141A] p-6" data-testid="theme-signs-card">
            <h3 className="font-display text-lg font-bold uppercase text-white">
              Sinais de alerta <span className="italic text-[#FFB800]">(6 cards)</span>
            </h3>
            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              {(cfg.signs || []).map((s, i) => (
                <div key={i} className="rounded-xl border border-white/10 p-4" data-testid={`theme-sign-${i}`}>
                  <input value={s.title} onChange={(e) => setListItem("signs", i, "title", e.target.value)} data-testid={`theme-sign-title-${i}`} className={inputCls} />
                  <textarea rows={2} value={s.desc} onChange={(e) => setListItem("signs", i, "desc", e.target.value)} data-testid={`theme-sign-desc-${i}`} className={`${inputCls} mt-2 resize-none`} />
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-white/5 bg-[#11141A] p-6" data-testid="theme-risks-card">
            <h3 className="font-display text-lg font-bold uppercase text-white">
              Riscos <span className="italic text-[#FFB800]">(seção escura, 6 cards)</span>
            </h3>
            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              {(cfg.risks || []).map((r, i) => (
                <div key={i} className="rounded-xl border border-white/10 p-4" data-testid={`theme-risk-${i}`}>
                  <input value={r.title} onChange={(e) => setListItem("risks", i, "title", e.target.value)} data-testid={`theme-risk-title-${i}`} className={inputCls} />
                  <textarea rows={2} value={r.desc} onChange={(e) => setListItem("risks", i, "desc", e.target.value)} data-testid={`theme-risk-desc-${i}`} className={`${inputCls} mt-2 resize-none`} />
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-white/5 bg-[#11141A] p-6" data-testid="theme-steps-card">
            <h3 className="font-display text-lg font-bold uppercase text-white">
              Como funciona <span className="italic text-[#FFB800]">(6 passos)</span>
            </h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {(cfg.steps || []).map((s, i) => (
                <div key={i} data-testid={`theme-step-${i}`}>
                  <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Passo {i + 1}</label>
                  <input value={s.label} onChange={(e) => setListItem("steps", i, "label", e.target.value)} data-testid={`theme-step-label-${i}`} className={`${inputCls} mt-1.5`} />
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-white/5 bg-[#11141A] p-6" data-testid="theme-faq-card">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-bold uppercase text-white">
                Perguntas <span className="italic text-[#FFB800]">Frequentes</span>
              </h3>
              <button
                onClick={() => set("faqs", [...(cfg.faqs || []), { q: "", a: "" }])}
                disabled={(cfg.faqs || []).length >= 12}
                data-testid="theme-faq-add"
                className="flex items-center gap-1.5 rounded-full border border-[#FFB800]/50 px-4 py-2 text-xs font-bold uppercase tracking-wider text-[#FFB800] transition-colors hover:bg-[#FFB800]/10 disabled:opacity-50"
              >
                <Plus size={13} strokeWidth={3} /> Nova pergunta
              </button>
            </div>
            <div className="mt-4 space-y-4">
              {(cfg.faqs || []).map((f, i) => (
                <div key={i} className="flex items-start gap-2" data-testid={`theme-faq-${i}`}>
                  <div className="flex-1 space-y-2">
                    <input value={f.q} onChange={(e) => setListItem("faqs", i, "q", e.target.value)} data-testid={`theme-faq-q-${i}`} placeholder={`Pergunta ${i + 1}`} className={inputCls} />
                    <textarea rows={2} value={f.a} onChange={(e) => setListItem("faqs", i, "a", e.target.value)} data-testid={`theme-faq-a-${i}`} placeholder="Resposta" className={`${inputCls} resize-none`} />
                  </div>
                  <button onClick={() => set("faqs", cfg.faqs.filter((_, j) => j !== i))} data-testid={`theme-faq-delete-${i}`} className="mt-2 text-zinc-600 transition-colors hover:text-red-400" aria-label="Remover pergunta">
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "previa" && (
        <div className="rounded-2xl border border-white/5 bg-[#11141A] p-4" data-testid="theme-preview-card">
          <p className="mb-3 px-2 text-xs text-zinc-500">
            Prévia ao vivo — muda enquanto você edita, sem salvar. Para publicar no site, clique em "Salvar Tudo".
          </p>
          <iframe
            ref={previewRef}
            src="/?preview=1"
            onLoad={postPreview}
            title="Prévia do site"
            data-testid="theme-preview-iframe"
            className="h-[75vh] w-full rounded-xl border border-white/10 bg-white"
          />
        </div>
      )}
    </div>
  );
}
